import * as React from 'react';
import { TextField, InputAdornment, type TextFieldProps } from '@mui/material';
import { MoneyTextFieldProps } from './types';

const sanitize = (raw: string) => {
  let s = raw.replace(/[^\d.\-]/g, '');
  if (s.includes('-')) s = (s.startsWith('-') ? '-' : '') + s.replace(/-/g, '');
  const i = s.indexOf('.');
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, '');
  return s;
};

const limit2dp = (s: string) => {
  const [int, dec] = s.split('.');
  if (dec === undefined) return int;
  return `${int}.${dec.slice(0, 2)}`;
};

const toNumberOrNull = (s: string): number | null => {
  if (s === '' || s === '-' || s === '.') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const groupForTyping = (raw: string, locale: string): string => {
  if (raw === '' || raw === '-' || raw === '.') return raw;
  const neg = raw.startsWith('-');
  const body = neg ? raw.slice(1) : raw;
  const [int, dec] = body.split('.');

  const groupedInt =
    int === ''
      ? ''
      : new Intl.NumberFormat('en-IN', {
          useGrouping: true,
          maximumFractionDigits: 0,
        }).format(Number(int));

  let out = (neg ? '-' : '') + groupedInt;
  if (body.endsWith('.') && dec === undefined) out += '.';
  else if (dec !== undefined) out += '.' + dec;
  return out;
};

const formatFixed2 = (n: number, locale: string) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(n);

const countDigits = (s: string, end: number) => (s.slice(0, end).match(/\d/g) || []).length;

const indexFromDigitCount = (s: string, k: number) => {
  if (k <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < s.length; i++) {
    if (/\d/.test(s[i])) seen++;
    if (seen >= k) return i + 1;
  }
  return s.length;
};

export const MoneyTextField: React.FC<MoneyTextFieldProps> = ({
  value: valueProp,
  defaultValue = null,
  currencySymbol = '',
  locale = 'en-IN',
  InputProps,
  inputProps,
  onBlur,
  onFocus,
  onChange,
  ...rest
}) => {
  const isControlled = valueProp !== undefined;

  const [internalValue, setInternalValue] = React.useState<number | null>(defaultValue);

  const numericValue = isControlled ? valueProp ?? null : internalValue;

  const [editing, setEditing] = React.useState<string>('');
  const [focused, setFocused] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const display = focused ? editing : numericValue == null ? '0.00' : formatFixed2(numericValue, locale);

  React.useEffect(() => {
    if (!focused) {
      if (numericValue == null) setEditing('');
      else setEditing(groupForTyping(String(numericValue), locale));
    }
  }, [numericValue, focused, locale]);

  const setNumeric = (n: number | null) => {
    if (!isControlled) setInternalValue(n);
    onChange?.(n);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const el = e.target;
    const incoming = el.value;
    const caret = el.selectionStart ?? incoming.length;

    const digitsBefore = countDigits(incoming, caret);

    let raw = incoming.replace(/,/g, '');
    raw = limit2dp(sanitize(raw));

    const grouped = groupForTyping(raw, locale);
    setEditing(grouped);

    setNumeric(toNumberOrNull(raw));

    requestAnimationFrame(() => {
      const node = inputRef.current;
      if (!node) return;

      let pos: number;

      if (incoming.endsWith('.') && caret === incoming.length) {
        pos = grouped.length;
      } else if (incoming.startsWith('-') && caret === 1) {
        pos = 1;
      } else if (grouped === '.' || grouped === '-.') {
        pos = grouped.length;
      } else if (digitsBefore === 0 && grouped.includes('.') && (grouped.startsWith('.') || grouped.startsWith('-.'))) {
        pos = grouped.indexOf('.') + 1;
      } else {
        pos = indexFromDigitCount(grouped, digitsBefore);
      }

      node.setSelectionRange(pos, pos);
    });
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    setEditing((prev) => groupForTyping(prev.replace(/,/g, ''), locale));
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    const num = toNumberOrNull(editing.replace(/,/g, ''));
    if (num == null) {
      // setNumeric(null);
      setEditing('');
    } else {
      const rounded = Number(num.toFixed(2));
      // setNumeric(rounded);
      setEditing(groupForTyping(String(rounded), locale));
    }
    onBlur?.(e);
  };

  const mergedInputProps: TextFieldProps['InputProps'] = {
    ...(InputProps || {}),
    startAdornment:
      InputProps?.startAdornment ?? (currencySymbol ? <InputAdornment position="start">{currencySymbol}</InputAdornment> : undefined),
  };

  const mergedInnerInputProps: TextFieldProps['inputProps'] = {
    inputMode: 'decimal',
    ...inputProps,
    style: { textAlign: 'right', ...(inputProps?.style || {}) },
  };

  return (
    <TextField
      {...rest}
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      InputProps={mergedInputProps}
      inputProps={mergedInnerInputProps}
      inputRef={inputRef}
      size={rest.size ?? 'small'}
      sx={{
        '.MuiInputBase-input': { textAlign: 'right' },
        ...(rest.sx || {}),
      }}
    />
  );
};
